import { Grid, Paper } from '@mui/material'
import React, { useEffect, useState } from 'react'
import UtilizationMonthlyTrends from '../components/UtilizationMonthlyTrends'
import { LeasesBasicDetails, LeasesNav } from '../../elements'
import ModuleNotExcessModal from '../../../shared_elements/components/ModuleNotExcessModal'
import BackToLeaseList from '../../elements/BackToLeaseList'
import ClaimsAndShopVisits from '../components/ClaimsAndShopVisits'
import { useParams } from 'react-router'
import FilterCard from '../components/FilterCard'
import moment from 'moment'
import { backendDateFormat } from '../../../constants'
import CashflowAndForwardBilling from '../components/CashflowAndForwardBilling'
import useContractAnalyticsApiHook from '../hooks/useContractAnalyticsApiHook'
import InvoiceAndPaymentTrends from '../components/InvoiceAndPaymentTrends'
import ComplainceContractEventsRedelivery from '../components/ComplainceContractEventsRedelivery';
import MaintenanceReserveFunds from '../components/MaintenanceReserveFunds'
import { RoleBaseAccess } from '../../../hoc'
import ContractHealthOverView from '../components/ContractHealthOverView'
import { PageLoader } from '../../../shared_elements'

const ContractAnalytics = () => {
  const params = useParams();
  const [assetData,setAssetData]=useState({});
  const [utilType, setUtilType] = useState('both');
  const [utilVal, setUtilVal] = useState('quarterly')
  const [trendMetric, setTrendMetric] = useState('fh')
  const [invVal, setInvVal] = useState('monthly')
  const [invType, setInvType] = useState('1')
  const [invDateType,setInvDateType]=useState('due_date')
  const [eventType, setEventType] = useState('')
  const [cardFilters, setCardFilters] = useState({});
  const [filterData, setFilterData] = useState({});
  const [error, setError] = useState({});
  const [filterType, setFilterType] = useState('global');
  const { leaseBasicDetails, loading, data, currencies, dropDownLoader, getDefaultDateRange, getMonthlyUtilizationData, getAverageUtilizationData, getComponentUtilizationTrendsData, getInvPaymentTrendsData, getInvReceivablesData, getPaymentTimelinessData, getClaimsRecoveryData, getClaimApvTurnAroundData, getPlannedCompletedSVData, getCashflowWaterfallApiData, getCashflowForecastData, fetchAllContractAnalytics, getCurrencyData, getAmendmentHistoryData, getFundMovementWaterfallData, getMRFundBalanceByEventTypeData, } = useContractAnalyticsApiHook(params, utilType, utilVal, trendMetric);

  const getDateRangeQuery = (filter = null) => {
    const activeFilter = filter && Object.keys(filter).length ? filter : filterData;
    if (!activeFilter?.date_from && !activeFilter?.date_to) {
      return getDefaultDateRange();
    }
    if (activeFilter?.date_from && !activeFilter?.date_to) {
      return {
        date_from: moment(activeFilter.date_from).format(backendDateFormat),
        date_to: moment().format(backendDateFormat),
      };
    }
    if (!activeFilter?.date_from && activeFilter?.date_to) {
      return {
        date_from: moment(activeFilter.date_to).subtract(1, 'year').format(backendDateFormat),
        date_to: moment(activeFilter.date_to).format(backendDateFormat),
      };
    }
    return {
      date_from: moment(activeFilter.date_from).format(backendDateFormat),
      date_to: moment(activeFilter.date_to).format(backendDateFormat),
    };
  }

  const dateTypeQuery = (dateType) => (dateType ? { date_type: dateType } : {});

  const getCardQuery = (card, query = {}) => ({
    lease_slug: params?.leaseSlug,
    ...getDateRangeQuery(cardFilters[card]),
    display_currency_id: cardFilters[card]?.currency ?? filterData?.display_currency_id?.value ?? 1,
    ...query,
  })

  useEffect(()=>{
    fetchAllContractAnalytics({ lease_slug: params?.leaseSlug, ...getDefaultDateRange(), display_currency_id: 1 });
    getInvPaymentTrendsData(getCardQuery('invoice_payments', { ...dateTypeQuery(invDateType), frequency: invVal, invoice_type: invType }))
    getCurrencyData();
  },[])

  useEffect(() => {
    if (leaseBasicDetails?.asset){
      setAssetData({asset_id: leaseBasicDetails?.asset?.id, asset_type: leaseBasicDetails?.asset?.asset_type,  });
      getMonthlyUtilizationData(getCardQuery('monthly_utilization', { asset_id: leaseBasicDetails?.asset?.id, asset_type: leaseBasicDetails?.asset?.asset_type }))
    }
  }, [leaseBasicDetails])

  const onAssetChange=(asset)=>{
          setAssetData(asset);
          getMonthlyUtilizationData(getCardQuery('monthly_utilization', { asset_id: asset?.asset_id, asset_type: asset?.asset_type }))
  }
  const onMetricChange = (metric) => {
          setUtilType(metric);
  }
  const onPeriodChange = (period) => {
          setUtilVal(period);
          delete data?.average_utilization;
          getAverageUtilizationData(getCardQuery('average_utilization', { period }))
  }
  const onTrendMetricChange = (metric) => {
          setTrendMetric(metric);
          delete data?.component_utilization_trends;
          getComponentUtilizationTrendsData(getCardQuery('component_utilization_trends', { type: metric }))
  }
  const onInvoiceDateTypeChange = (dateType, siblings = {}) => {
          setInvDateType(dateType);
          delete data?.invoice_payments;
          getInvPaymentTrendsData(getCardQuery('invoice_payments', { ...dateTypeQuery(dateType), frequency: siblings.frequency ?? invVal, invoice_type: siblings.invoice_type ?? invType }))
  }
  const onInvTypeChange = (InvoiceType, siblings = {}) => {
          setInvType(InvoiceType);
          delete data?.invoice_payments;
          getInvPaymentTrendsData(getCardQuery('invoice_payments', { invoice_type: InvoiceType, frequency: siblings.frequency ?? invVal, ...dateTypeQuery(siblings.date_type ?? invDateType) }))
  }
  const onInvoicePeriodChange = (frequency, siblings = {}) => {
          setInvVal(frequency);
          delete data?.invoice_payments;
          getInvPaymentTrendsData(getCardQuery('invoice_payments', { ...dateTypeQuery(siblings.date_type ?? invDateType), frequency, invoice_type: siblings.invoice_type ?? invType }))
  }
  const onEventTypeChange = (value) => {
    setEventType(value);
    getFundMovementWaterfallData(getCardQuery('fund_movement_waterfall', { ...(value && { event_type: value }) }))
  }
  const onCardFilterChange = (card, filter) => {
    setCardFilters(prev => ({ ...prev, [card]: filter }));
    const query = {
      lease_slug: params?.leaseSlug,
      ...getDateRangeQuery(filter),
      display_currency_id: filter?.currency ?? filterData?.display_currency_id?.value ?? 1,
    };
    switch (card) {
      case 'monthly_utilization': return getMonthlyUtilizationData({ ...query, asset_id: assetData?.asset_id, asset_type: assetData?.asset_type });
      case 'component_utilization_trends': return getComponentUtilizationTrendsData({ ...query, type: trendMetric });
      case 'average_utilization': return getAverageUtilizationData({ ...query, period: utilVal });
      case 'invoice_payments': return getInvPaymentTrendsData({ ...query, ...dateTypeQuery(invDateType), frequency: invVal, invoice_type: invType });
      case 'invoice_receivables': return getInvReceivablesData(query);
      case 'payment_timeliness': return getPaymentTimelinessData(query);
      case 'cashflow_waterfall': return getCashflowWaterfallApiData(query);
      case 'cashflow_forecast': return getCashflowForecastData(query);
      case 'fund_movement_waterfall': return getFundMovementWaterfallData({ ...query, ...(eventType && { event_type: eventType }) });
      case 'mr_fund_balance_by_event_type': return getMRFundBalanceByEventTypeData({ ...query, as_of_date: query.date_to });
      case 'claim_status_recovery': return getClaimsRecoveryData(query);
      case 'claim_approval_turnaround': return getClaimApvTurnAroundData(query);
      case 'planned_shop_visits': return getPlannedCompletedSVData(query);
      case 'amendment_history': return getAmendmentHistoryData(query);
      default: return undefined;
    }
  }

  const onFieldChange = (key, value) => setFilterData(prev => ({ ...prev, [key]: value }));
  const onFocusChange = (key) => setError(prev => ({ ...prev, [key]: '' }));

  const validateDate = (value, fieldLabel) => {
    if (!value) return '';
    const date = moment(value);
    const leaseStartDate = moment(leaseBasicDetails?.start_date);
    const leaseEndDate = moment(leaseBasicDetails?.end_date);
    if (!date.isValid()) return `Please select valid ${fieldLabel}`;
    if (!leaseStartDate.isValid() || !leaseEndDate.isValid()) return '';
    return date.isBetween(leaseStartDate, leaseEndDate, 'day', '[]') ? '' : `${fieldLabel} must be within the lease period`;
  };

  const onApplyFilter = () => {
    const validationInputs = {
      date_from: validateDate(filterData?.date_from, 'Date From'),
      date_to: validateDate(filterData?.date_to, 'Date To'),
    };
    const dateFrom = filterData?.date_from ? moment(filterData.date_from) : null;
    const dateTo = filterData?.date_to ? moment(filterData.date_to) : null;
    if (dateFrom && dateTo && dateTo.isBefore(dateFrom)) {
      validationInputs.date_from = 'Date From cannot be before Date To';
      validationInputs.date_to = 'Date To cannot be after Date From';
    }
    if (Object.values(validationInputs).some(Boolean)) return setError(validationInputs);

    const query = {
      lease_slug: params?.leaseSlug,
      ...getDateRangeQuery(filterData),
      display_currency_id: filterData?.display_currency_id?.value ?? 1,
    };
    setCardFilters({});
    fetchAllContractAnalytics(query);
    if (assetData?.asset_id) getMonthlyUtilizationData({ ...query, asset_id: assetData.asset_id, asset_type: assetData.asset_type });
    getInvPaymentTrendsData({ ...query, ...dateTypeQuery(invDateType), frequency: invVal, invoice_type: invType });
    getFundMovementWaterfallData({ ...query, ...(eventType && { event_type: eventType }) });
  };

  const onResetFilter = () => {
    setError({});
    setFilterData({});
    setCardFilters({});
    const query = { lease_slug: params?.leaseSlug, ...getDefaultDateRange(), display_currency_id: 1 };
    fetchAllContractAnalytics(query);
    if (assetData?.asset_id) getMonthlyUtilizationData({ ...query, asset_id: assetData.asset_id, asset_type: assetData.asset_type });
    getInvPaymentTrendsData({ ...query, ...dateTypeQuery(invDateType), frequency: invVal, invoice_type: invType });
    getFundMovementWaterfallData({ ...query, ...(eventType && { event_type: eventType }) });
  };

  const onFilterTypeChange = (value) => {
    setFilterType(value);
    if (value === 'graph') {
      onResetFilter();
    } else {
      onApplyFilter();
    }
  };

  return (
    <>
      <LeasesBasicDetails />
      <Paper className='lease-details-section lease-nav-module-margin' elevation={0} sx={{ boxShadow: "0px 0px 2px 1px #BBBBBB" }} square>
        <LeasesNav />
        {leaseBasicDetails?.status === 'Draft' ? (<ModuleNotExcessModal moduleName="Claim Management" />) : leaseBasicDetails && Object.keys(leaseBasicDetails).length > 0 && !leaseBasicDetails?.status ? (<ModuleNotExcessModal leaseNotFound />) :
          <div className='lease-details-content lease-content-overflow'>
            <Paper square className="responsive-paper">
              <Grid container spacing={1} className="responsive-grid" alignItems='end'>
                <Grid item size={{ md: 12, xs: 12 }} sx={{ marginBottom: "-15px", marginLeft: "3px" }} className="menu-container lease-list-link-mt">
                  <BackToLeaseList />
                </Grid>
              </Grid>
            </Paper>
            <Paper sx={{ margin: '10px 0px 5px 0px', padding: "5px", background: '#f9fafb', height: "calc(100vh - 200px)", overflow: 'auto' }}>
              <Grid container>
                <Grid size={{ md: 12, xs: 12 }}>
                  <FilterCard dropDownLoader={dropDownLoader} filterData={filterData} onFieldChange={onFieldChange} onFocusChange={onFocusChange} onApplyFilter={onApplyFilter} onResetFilter={onResetFilter} error={error} currencies={currencies} filterType={filterType} onFilterTypeChange={onFilterTypeChange} />
                </Grid>
                <Grid size={{ md: 12, xs: 12 }} mb={1}>
                  <ContractHealthOverView data={data} loading={loading} />
                </Grid>
                <Grid item size={{ md: 12, xs: 12 }}>
                  <UtilizationMonthlyTrends utilizationData={data} assetData={assetData} onAssetChange={onAssetChange} utilType={utilType} onMetricChange={onMetricChange} utilVal={utilVal} onPeriodChange={onPeriodChange} trendMetric={trendMetric} onTrendMetricChange={onTrendMetricChange} isLoading={loading} filters={cardFilters} onFilterChange={onCardFilterChange} cardFiltersDisabled={filterType === 'global'} />
                </Grid>
                <Grid item size={{ md: 12, xs: 12 }}>
                  <ComplainceContractEventsRedelivery data={data} loading={loading} />
                </Grid>
                <Grid item size={{ md: 12, xs: 12 }}>
                  <InvoiceAndPaymentTrends invPaymentsData={data} invVal={invVal} invDateType={invDateType} invType={invType} onInvDateTypeChange={onInvoiceDateTypeChange} onInvPeriodChange={onInvoicePeriodChange} onInvTypeChange={onInvTypeChange} isLoading={loading} filters={cardFilters} onFilterChange={onCardFilterChange} currencies={currencies} cardFiltersDisabled={filterType === 'global'} />
                </Grid>
                <Grid item size={{ md: 12, xs: 12 }}>
                  <CashflowAndForwardBilling cashflowData={data} loading={loading} filters={cardFilters} onFilterChange={onCardFilterChange} currencies={currencies} cardFiltersDisabled={filterType === 'global'} />
                </Grid>
                <Grid item size={{ md: 12, xs: 12 }}>
                  <MaintenanceReserveFunds maintenanceData={data} isLoading={loading} eventType={eventType} onEventTypeChange={onEventTypeChange} leaseBasicDetails={leaseBasicDetails} filters={cardFilters} onFilterChange={onCardFilterChange} currencies={currencies} cardFiltersDisabled={filterType === 'global'} />
                </Grid>
                <Grid item size={{ md: 12, xs: 12 }}>
                  <ClaimsAndShopVisits claimsData={data} loading={loading} filters={cardFilters} onFilterChange={onCardFilterChange} currencies={currencies} cardFiltersDisabled={filterType === 'global'} />
                </Grid>
                
              </Grid>
            </Paper>
          </div>}
      </Paper>
      {loading?.claim_status_recovery ? <PageLoader /> : null }
    </>
  )
}

export default RoleBaseAccess(ContractAnalytics, { application: 'lease_revamp', feature: 'statistics', operationType: ['R'] });